import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MotifButton, MotifFormField, MotifInput, MotifSelect, MotifOption, MotifLabel } from '@ey-xd/motif-react';
import { MotifToggleSwitch } from '@ey-xd/motif-react';
import { MotifIcon } from '@ey-xd/motif-react';
import { navigationIcArrowBack24px } from "@ey-xd/motif-react/assets/icons";
import './AddUser.scss';
import { MotifSearch } from '@ey-xd/motif-react';
import { UserService } from 'services/user.service';
import { MAX_INPUT_LENGTH } from 'components/Clients/constants';
import { getRoles } from '../../redux/actions/UserManagement/userManagement';

type AddUserForm = {
    UserFullName: string;
    UserEmail: string;
    Role: string;
    role_id?: string;
    IsActive: boolean;
    UserId?: string;
};

type AddUserProps = {
    form: AddUserForm;
    setForm: (updater: any) => void;
    onSave: () => void;
    onCancel: () => void;
    editItem?: any | null;
};

function AddUser({ form, setForm, onSave, onCancel, editItem }: AddUserProps) {
    const dispatch = useDispatch();
    const isSaveDisabled = !form.UserFullName || !form.UserEmail || (!form.Role && !form.role_id);
    const isEdit = !!editItem;
    const loggedInEmail = (sessionStorage.getItem('user_mail') || '').toLowerCase();
    const isSelf = isEdit && (form.UserEmail || '').toLowerCase() === loggedInEmail;
    const [searchValue, setSearchValue] = useState<string>('');
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const debounceRef = useRef<any>(null);
    const roles = useSelector((s: any) => s?.userManagement?.roles?.data) || [];
    const isLoadingRoles = useSelector((s: any) => s?.userManagement?.roles?.loading) || false;

    const fetchSuggestions = async (q: string) => {
        if (!q || q.trim().length < 2) {
            setSuggestions([]);
            return;
        }
        try {
            setIsSearching(true);
            const res = await UserService.searchAdUsers(q.trim());
            const list = (res as any)?.value?.data ?? (res as any)?.data ?? [];
            setSuggestions(Array.isArray(list) ? list : []);
        } catch (e) {
            setSuggestions([]);
        } finally {
            setIsSearching(false);
        }
    };

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchSuggestions(searchValue), 300);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [searchValue]);

    useEffect(() => {
        // In edit mode, if only Role name is present, derive role_id from loaded roles
        if (editItem && (!form.role_id || String(form.role_id).length === 0) && form.Role && Array.isArray(roles) && roles.length > 0) {
            const match = roles.find((r: any) => {
                const name = r?.RoleName || r?.name || r?.Role || r?.role;
                return String(name || '').toLowerCase() === String(form.Role || '').toLowerCase();
            });
            if (match) {
                const id = match?.role_id || match?.RoleId || match?.id || match?.Id || match?.value;
                if (id !== undefined && id !== null) {
                    setForm((p: any) => ({ ...p, role_id: String(id) }));
                }
            }
        }
    }, [editItem, roles, form.Role]);

    const getDisplayName = (u: any) => u?.fullName || u?.displayName || u?.UserFullName || u?.name || '';
    const getEmail = (u: any) => u?.email || u?.mail || u?.UserEmail || '';
    const onSelectSuggestion = (u: any) => {
        const fullName = getDisplayName(u);
        const email = getEmail(u);
        setForm((p: any) => ({ 
            ...p, 
            UserFullName: fullName || p.UserFullName, 
            UserEmail: email || p.UserEmail,
            UserId: u?.id || p.UserId,
            IsActive: typeof u?.status === 'boolean' ? u.status : p.IsActive,
        }));
        setSearchValue(fullName || email || '');
        setSuggestions([]);
    };

    return (
        <div className="addUserWrapper">
            <div
                className="backNavigation"
                onClick={onCancel}
            >
                <MotifIcon
                    src={navigationIcArrowBack24px}
                    style={{ width: 20 }}
                />
                <span>Back</span>
            </div>
            <div className="addUser__title">{editItem ? 'Edit User' : 'Add New User'}</div>
            <div className="addUser__card">
                <div className='addUser__section addUser__section--left'>
                    <div className="searchWithDropdown">
                        <MotifFormField>
                            <MotifSearch
                                value={searchValue || form.UserEmail}
                                id="user-search"
                                onChange={(event: any) => { if (!isEdit) setSearchValue(event.target.value); }}
                                aria-label="Search users"
                                aria-describedby="search-error"
                                placeholder={'Search by user email'}
                                isLoading={!isEdit && isSearching}
                                disabled={isEdit}
                                readOnly={isEdit}
                                onClear={() => { if (!isEdit) { setSearchValue(''); setSuggestions([]); } }} />
                            <div id="search-error" style={{ display: 'none' }} aria-live="polite"></div>
                        </MotifFormField>
                        {!isEdit && suggestions?.length > 0 && (
                            <div className="searchDropdown">
                                {suggestions.map((u: any, idx: number) => (
                                    <div key={idx} className="searchItem" onClick={() => onSelectSuggestion(u)}>
                                        <div className="primary">{getDisplayName(u) || getEmail(u)}</div>
                                        {getEmail(u) && <div className="secondary">{getEmail(u)}</div>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div>
                        <span>User Email (required)</span>
                        <MotifFormField>
                            {!form.UserEmail && <MotifLabel>User Email</MotifLabel>}
                            <MotifInput type="email" value={form.UserEmail} onChange={(e: any) => setForm((p: any) => ({ ...p, UserEmail: e.target.value }))} disabled={isEdit} maxLength={MAX_INPUT_LENGTH} />
                        </MotifFormField>
                    </div>
                    <div>
                        <span>User Full Name (required)</span>
                        <MotifFormField>
                            {!form.UserFullName && <MotifLabel>User Full Name</MotifLabel>}
                            <MotifInput value={form.UserFullName} onChange={(e: any) => setForm((p: any) => ({ ...p, UserFullName: e.target.value }))} disabled={isEdit} maxLength={MAX_INPUT_LENGTH} />
                        </MotifFormField>
                    </div>
                    <div>
                        <span>User Role (required)</span>
                        <MotifFormField>
                            <MotifSelect
                                value={form.role_id}
                                onChange={(v: any) => {
                                    const selectedId = String(v);
                                    const match = (roles || []).find((r: any) => {
                                        const id = r?.role_id || r?.RoleId || r?.id || r?.Id || r?.value;
                                        return String(id) === selectedId;
                                    });
                                    const roleName = match ? (match?.RoleName || match?.name || match?.displayName || match?.Role || match?.role) : selectedId;
                                    const isAdministrator = String(roleName || '').trim().toLowerCase() === 'administrator';
                                    if (isAdministrator) {
                                        // Block assigning Administrator
                                        return;
                                    }
                                    setForm((p: any) => ({ ...p, role_id: selectedId, Role: String(roleName) }));
                                }}
                                visibleOptions={5}
                            >
                                {roles?.length > 0 ? (
                                    roles.map((r: any, idx: number) => {
                                        const id = r?.role_id || r?.RoleId || r?.id || r?.Id || r?.value || r?.name || r?.RoleName;
                                        const label = r?.displayName || r?.RoleName || r?.name || r?.Role || r?.role || String(id);
                                        const isAdministrator = String(label || '').trim().toLowerCase() === 'administrator';
                                        return (
                                            <MotifOption key={idx} value={String(id)} disabled={isAdministrator}>{String(label)}</MotifOption>
                                        );
                                    })
                                ):<MotifOption value="">User Role</MotifOption>
                                }
                            </MotifSelect>
                        </MotifFormField>
                    </div>
                </div>
                <div className='addUser__section addUser__section--right'>
                    <div>
                        <MotifFormField style={{ display: 'flex', flexDirection: 'column' }}>
                            <MotifToggleSwitch
                                label="Active"
                                id="activeUserToggle"
                                value={form.IsActive}
                                checked={form.IsActive}
                                offLabel=""
                                onLabel={form.IsActive ? "Yes" : "No"}
                                disabled={isSelf}
                                onChange={() => setForm((p: any) => ({ ...p, IsActive: !p.IsActive }))}
                            ></MotifToggleSwitch>
                            <div style={{ position: 'relative', left: 48 }}>Is Active</div>
                        </MotifFormField>
                    </div>
                </div>
            </div>

            <div className="addApplicationProfile__actionsBar">
                <MotifButton variant="secondary" size="medium" type="button" onClick={onCancel}>Cancel</MotifButton>
                <div style={{display: 'flex', gap: 10}}>
                    <MotifButton variant="secondary" size="medium" type="button" onClick={onCancel}>
                        <MotifIcon src={navigationIcArrowBack24px} style={{ width: 20, marginRight: 6 }} />
                        Back
                    </MotifButton>
                    <MotifButton variant="primary" size="medium" type="button" onClick={onSave} disabled={isSaveDisabled}>
                        Save
                    </MotifButton>
                </div>
            </div>
        </div>
    );
}

export default AddUser;
